<template>
  <div class="login-container">
    <!-- Animated background with parallax effect -->
    <div class="background-image" :class="{ 'blur-active': isAuthenticating }"></div>
    
    <!-- Loading Overlay -->
    <transition name="loading-overlay">
      <div v-if="isAuthenticating" class="loading-overlay">
        <div class="loading-card">
          <div class="loading-animation">
            <div class="loading-ring">
              <div class="ring"></div>
              <div class="ring"></div>
              <div class="ring"></div>
              <div class="loading-logo">M</div>
            </div>
          </div>
          <div class="loading-text">
            <h3>{{ loadingMessage }}</h3>
            <div class="loading-dots">
              <span class="dot"></span>
              <span class="dot"></span>
              <span class="dot"></span>
            </div>
          </div>
          <div class="loading-progress">
            <div class="progress-bar" :style="{ width: loadingProgress + '%' }"></div>
          </div>
          <div class="loading-tips">
            <p class="tip-text">{{ currentTip }}</p>
          </div>
        </div>
      </div>
    </transition>

    <div class="login-content" :class="{ 'content-loaded': isContentLoaded, 'blur-content': isAuthenticating }">
      <!-- Login Form Section (LEFT CARD) - hides when reset password is shown -->
      <div class="login-section" :class="{ 'hidden-card': showResetPassword }">
        <transition name="slide-fade" appear>
          <div class="login-form-container">
            <div class="login-header">
              <transition name="fade-down" appear>
                <h1>Log in</h1>
              </transition>
              <transition name="fade-up" appear>
                <p>Welcome back! Please enter your details.</p>
              </transition>
            </div>

            <b-form @submit="onSubmit" autocomplete="off" class="login-form">
              <transition-group name="form-field" appear>
                <b-form-group
                  key="email"
                  id="username-label"
                  label="Email:"
                  label-for="username"
                  align="left"
                >
                  <b-form-input
                    id="username"
                    v-model="form.username"
                    type="text"
                    required
                    placeholder="Enter Email"
                    class="form-input"
                    :disabled="input_submit_disabled"
                    @focus="onFieldFocus"
                    @blur="onFieldBlur"
                  ></b-form-input>
                </b-form-group>

                <b-form-group
                  key="password"
                  id="password-label"
                  label="Password:"
                  label-for="password"
                  align="left"
                >
                  <div class="password-input-wrapper">
                    <b-form-input
                      id="password"
                      v-model="form.password"
                      :type="showPassword ? 'text' : 'password'"
                      required
                      placeholder="Enter Password"
                      class="form-input"
                      :disabled="input_submit_disabled"
                      @focus="onFieldFocus"
                      @blur="onFieldBlur"
                    ></b-form-input>
                    <button 
                      type="button" 
                      class="password-toggle-btn"
                      @click="togglePasswordVisibility"
                      :disabled="input_submit_disabled"
                    >
                      <span v-if="!showPassword" class="eye-icon">👁️</span>
                      <span v-else class="eye-icon">👁️‍🗨️</span>
                    </button>
                  </div>
                </b-form-group>

                <b-form-group 
                  v-if="showLocationSelector"
                  key="location"
                  id="location-label" 
                  label="Select Location:" 
                  label-for="location"
                  align="left"
                  class="location-selector"
                >
                  <b-form-select
                    id="location"
                    v-model="selectedLocation"
                    :options="locationOptions"
                    required
                    class="form-input"
                    :disabled="input_submit_disabled"
                  >
                    <template v-slot:first>
                      <b-form-select-option :value="null" disabled>-- Please select a location --</b-form-select-option>
                    </template>
                  </b-form-select>
                </b-form-group>
              </transition-group>

              <transition name="fade" appear>
                <div class="form-options">
                  <b-form-checkbox
                    id="remember-me"
                    v-model="form.rememberMe"
                    name="remember-me"
                    class="remember-checkbox"
                    :disabled="input_submit_disabled"
                  >
                    Remember me
                  </b-form-checkbox>
                  <a href="#" class="forgot-password" @click.prevent="showResetPasswordForm">Forgot Password?</a>
                </div>
              </transition>

              <transition name="button-pulse" appear>
                <b-button 
                  :disabled="input_submit_disabled || (showLocationSelector && !selectedLocation)" 
                  type="submit" 
                  variant="primary" 
                  class="login-button"
                  :class="{ 'loading': input_submit_disabled }"
                >
                  <span v-if="!input_submit_disabled" class="button-text">{{ showLocationSelector ? 'Select Location & Sign In' : 'Sign In' }}</span>
                  <span v-else class="button-loader">
                    <span class="loader-dot"></span>
                    <span class="loader-dot"></span>
                    <span class="loader-dot"></span>
                  </span>
                </b-button>
              </transition>
            </b-form>

            <!-- Failed Attempts Warning - Shows after 2 attempts -->
            <transition name="fade-up">
              <div v-if="failedAttempts === 2" class="attempts-warning">
                <span class="warning-icon">⚠️</span>
                <span>Last try! You'll be redirected to reset password after one more failed attempt.</span>
              </div>
            </transition>
          </div>
        </transition>
      </div>
      
      <!-- Brand Section (RIGHT CARD) - slides to LEFT when reset password is shown -->
      <div class="brand-section" :class="{ 'slide-to-left': showResetPassword }">
        <transition name="brand-slide" appear>
          <div class="brand-content">
            <div class="university-logos">
              <div class="university-logo">
                <transition name="logo-reveal">
                  <h2>MY FURNITURE MECCA</h2>
                </transition>
                <transition name="logo-reveal" appear>
                  <p>RUGS & MATTRESSES</p>
                </transition>
              </div>
            </div>
            
            <div class="divider"></div>
            <div class="creek-section">
              <transition name="text-reveal">
                <h3>INVENTORY SYSTEM</h3>
              </transition>
            </div>
            
            <div class="divider"></div>
            <div class="austin-section">
              <transition name="text-reveal">
                <h3>ENVIO BY VISTA</h3>
              </transition>
              <div class="venues">
                <transition-group name="venue-item" appear>
                  <div class="venue-item" key="envio">
                    <strong>ENVIO DISPATCH SOLUTION</strong>
                  </div>
                </transition-group>
              </div>
            </div>
          </div>
        </transition>
      </div>

      <!-- Reset Password Section (appears from RIGHT when visible) -->
      <div class="reset-password-section" :class="{ 'visible': showResetPassword }">
        <transition name="reset-slide">
          <div v-if="showResetPassword" class="reset-password-container">
            <button class="back-to-login" @click="hideResetPasswordForm">
              ← Back to Login
            </button>
            <div class="reset-password-header">
              <div class="reset-icon">🔐</div>
              <h2>Reset Password</h2>
              <p>Enter your email address and we'll send you a link to reset your password.</p>
            </div>
            
            <b-form @submit="sendPasswordResetRequest" class="reset-password-form">
              <b-form-group label="Email Address:" label-for="reset-email" align="left">
                <b-form-input
                  id="reset-email"
                  v-model="resetEmail"
                  type="email"
                  required
                  placeholder="Enter your email address"
                  class="form-input"
                  :disabled="resetPasswordSending"
                  autocomplete="off"
                ></b-form-input>
              </b-form-group>
              
              <b-button 
                type="submit" 
                variant="primary" 
                class="reset-button"
                :class="{ 'loading': resetPasswordSending }"
                :disabled="resetPasswordSending || !resetEmail"
              >
                <span v-if="!resetPasswordSending">Send Reset Link</span>
                <span v-else class="button-loader">
                  <span class="loader-dot"></span>
                  <span class="loader-dot"></span>
                  <span class="loader-dot"></span>
                </span>
              </b-button>
            </b-form>
            
            <div class="reset-info">
              <p>We'll send a password reset link to your email address.</p>
            </div>
          </div>
        </transition>
      </div>
    </div>

    <!-- Success Toast Animation -->
    <transition name="toast-slide">
      <div v-if="showSuccessToast" class="custom-toast success">
        <div class="toast-icon">✓</div>
        <div class="toast-content">
          <div class="toast-title">Success!</div>
          <div class="toast-message">{{ toastMessage }}</div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import Requestor from '@/utilities/Requestor'

export default {
  name: "Login",
  data() {
    return {
      form: {
        username: null,
        password: null,
        rememberMe: false
      },
      selectedLocation: null,
      locationOptions: [],
      showLocationSelector: false,
      input_submit_disabled: false,
      tempUserDetails: null,
      isContentLoaded: false,
      showSuccessToast: false,
      toastMessage: '',
      focusedField: null,
      isAuthenticating: false,
      loadingMessage: 'Authenticating',
      loadingProgress: 0,
      currentTipIndex: 0,
      loadingInterval: null,
      showPassword: false,
      showResetPassword: false,
      resetEmail: '',
      resetPasswordSending: false,
      
      // NEW: Failed login attempt tracking (3 strikes and you're out)
      failedAttempts: 0,
      // Track the last failed username to reset attempts if a different user tries
      lastFailedUsername: null,
      
      tips: [
        "🔐 Securely logging you in...",
        "✨ Preparing your dashboard...",
        "📦 Fetching inventory data...",
        "🚚 Loading warehouse locations...",
        "⚡ Almost there!",
        "🎯 Setting up your preferences..."
      ]
    }
  },
  computed: {
    currentTip() {
      return this.tips[this.currentTipIndex];
    }
  },
  mounted() {
    setTimeout(() => {
      this.isContentLoaded = true;
    }, 100);
  },
  beforeDestroy() {
    if (this.loadingInterval) {
      clearInterval(this.loadingInterval);
    }
  },
  methods: {
    // NEW: Reset failed attempts when user starts typing a different email
    onFieldFocus() {
      // If the username field is focused and the current username is different from the last failed username,
      // reset failed attempts (new user trying to log in)
      if (this.form.username && this.lastFailedUsername && this.form.username !== this.lastFailedUsername) {
        this.failedAttempts = 0;
        this.lastFailedUsername = null;
      }
    },
    
    // NEW: Handle failed login attempt
    handleFailedAttempt() {
      // Increment failed attempts counter
      this.failedAttempts++;
      this.lastFailedUsername = this.form.username;
      
      // Show warning toast
      const remainingAttempts = 3 - this.failedAttempts;
      if (remainingAttempts > 0) {
        this.$bvToast.toast(
          `Invalid credentials. You have ${remainingAttempts} attempt${remainingAttempts !== 1 ? 's' : ''} remaining before password reset is required.`, 
          {title: "Login Failed", autoHideDelay: 3000, variant: "warning"}
        );
      } else if (this.failedAttempts >= 3) {
        // On 3rd failed attempt, automatically redirect to password reset
        this.$bvToast.toast(
          "Too many failed attempts. Please reset your password to continue.", 
          {title: "Access Restricted", autoHideDelay: 4000, variant: "danger"}
        );
        
        // Auto-redirect to reset password after a short delay
        setTimeout(() => {
          // Pre-fill the reset email with the username that failed
          this.resetEmail = this.form.username || '';
          this.showResetPassword = true;
          
          // Reset failed attempts count for next time
          this.failedAttempts = 0;
          this.lastFailedUsername = null;
        }, 1500);
      }
    },
    
    showResetPasswordForm() {
      // Optional: Pre-fill email if there's a username in the form
      if (this.form.username) {
        this.resetEmail = this.form.username;
      }
      this.showResetPassword = true;
    },
    
    hideResetPasswordForm() {
      this.showResetPassword = false;
      // Reset failed attempts when returning to login
      this.failedAttempts = 0;
      this.lastFailedUsername = null;
    },
    
    togglePasswordVisibility() {
      this.showPassword = !this.showPassword;
    },
    
    startLoadingAnimation() {
      this.isAuthenticating = true;
      this.loadingProgress = 0;
      this.currentTipIndex = 0;
      
      this.loadingInterval = setInterval(() => {
        if (this.loadingProgress < 90) {
          this.loadingProgress += Math.random() * 10;
          if (this.loadingProgress > 90) this.loadingProgress = 90;
        }
      }, 300);
      
      let tipInterval = setInterval(() => {
        if (this.currentTipIndex < this.tips.length - 1) {
          this.currentTipIndex++;
        } else {
          clearInterval(tipInterval);
        }
      }, 2000);
      
      let messages = ['Authenticating', 'Verifying credentials', 'Loading workspace', 'Preparing dashboard'];
      let msgIndex = 0;
      let msgInterval = setInterval(() => {
        if (msgIndex < messages.length - 1) {
          msgIndex++;
          this.loadingMessage = messages[msgIndex];
        } else {
          clearInterval(msgInterval);
        }
      }, 1500);
      
      this.tipInterval = tipInterval;
      this.msgInterval = msgInterval;
    },
    
    stopLoadingAnimation(success = true) {
      if (success) {
        this.loadingProgress = 100;
        setTimeout(() => {
          this.isAuthenticating = false;
          if (this.loadingInterval) clearInterval(this.loadingInterval);
          if (this.tipInterval) clearInterval(this.tipInterval);
          if (this.msgInterval) clearInterval(this.msgInterval);
        }, 500);
      } else {
        this.isAuthenticating = false;
        if (this.loadingInterval) clearInterval(this.loadingInterval);
        if (this.tipInterval) clearInterval(this.tipInterval);
        if (this.msgInterval) clearInterval(this.msgInterval);
      }
    },
    
    onFieldBlur() {
      this.focusedField = null;
    },
    
    showToast(message, type = 'success') {
      this.toastMessage = message;
      this.showSuccessToast = true;
      setTimeout(() => {
        this.showSuccessToast = false;
      }, 3000);
    },
    
    onSubmit: function(e) {
      e.preventDefault();
      
      if (!this.showLocationSelector) {
        this.authenticateUser();
      } else {
        this.completeLoginWithLocation();
      }
    },
    
    authenticateUser: function() {
      let that = this;
      let path = "/api/user/login/";
      let data = {
        username: this.form.username,
        password: this.form.password
      };

      this.startLoadingAnimation();

      let s = function(response) {
        that.input_submit_disabled = false;
        
        if (response.data) {
          if (response.data.error) {
            that.stopLoadingAnimation(false);
            // NEW: Handle failed attempt on authentication error
            that.handleFailedAttempt();
            return;
          }
          
          if (response.data.needs_location_selection) {
            that.stopLoadingAnimation(true);
            // Reset failed attempts on successful credential validation
            that.failedAttempts = 0;
            that.lastFailedUsername = null;
            setTimeout(() => {
              that.tempUserDetails = response.data.user_details;
              that.locationOptions = response.data.location_options.map(loc => ({
                value: loc.value,
                text: loc.text
              }));
              that.showLocationSelector = true;
              that.$bvToast.toast(
                "Please select a location to continue.", 
                {title: "Location Required", autoHideDelay: 3000, variant: "info"}
              );
            }, 500);
          } else {
            // Reset failed attempts on successful login
            that.failedAttempts = 0;
            that.lastFailedUsername = null;
            let userToken = response.data.token;
            if (userToken) {
              localStorage.setItem("userToken", userToken);
              localStorage.setItem("userDetails", JSON.stringify(response.data.user_details));
              if (response.data.selected_location) {
                localStorage.setItem("selectedLocation", response.data.selected_location);
              }
              that.$root.$emit("authenticated", true);
              that.showToast('Login successful! Redirecting...', 'success');
              setTimeout(() => {
                that.fetch_prereqs();
              }, 1000);
            }
          }
        }
      };

      let f = function(error) {
        let errorMessage = error.error || "Login failed. Please try again.";
        if (error.response && error.response.data && error.response.data.error) {
          errorMessage = error.response.data.error;
        }
        
        that.stopLoadingAnimation(false);
        // NEW: Handle failed attempt on network/API error
        that.handleFailedAttempt();
        that.input_submit_disabled = false;
      };
      
      that.input_submit_disabled = true;
      Requestor.postSync(path, data, s, f);
    },
    
    completeLoginWithLocation: function() {
      if (!this.selectedLocation) {
          this.$bvToast.toast(
              "Please select a location.", 
              {title: "Location Required", autoHideDelay: 1500, variant: "warning"}
          );
          return;
      }
      
      let that = this;
      let path = "/api/user/complete_login/";
      let data = {
          user_id: this.tempUserDetails.id,
          selected_location: this.selectedLocation,
          username: this.form.username
      };

      this.startLoadingAnimation();
      this.loadingMessage = "Selecting location...";

      let s = function(response) {
          that.input_submit_disabled = false;
          
          if (response.data) {
              if (response.data.error) {
                  that.stopLoadingAnimation(false);
                  that.$bvToast.toast(
                      response.data.error, 
                      {title: "Login Failed!", autoHideDelay: 1500, variant: "danger"}
                  );
                  return;
              }
              
              let userToken = response.data.token;
              if (userToken) {
                  localStorage.setItem("userToken", userToken);
                  
                  let userDetails = response.data.user_details;
                  userDetails.location_id = that.selectedLocation;
                  
                  if (userDetails.properties) {
                      userDetails.properties.location_id = that.selectedLocation;
                  }
                  
                  localStorage.setItem("userDetails", JSON.stringify(userDetails));
                  localStorage.setItem("selectedLocation", that.selectedLocation);
                  localStorage.setItem("userLocations", JSON.stringify(response.data.user_locations || []));
                  that.$root.$emit("authenticated", true);
                  that.showToast('Location selected! Welcome aboard!', 'success');
                  setTimeout(() => {
                    that.fetch_prereqs();
                  }, 1000);
              }
          }
      };

      let f = function(error) {
          let errorMessage = error.error || "Login failed. Please try again.";
          if (error.response && error.response.data && error.response.data.error) {
              errorMessage = error.response.data.error;
          }
          
          that.stopLoadingAnimation(false);
          that.$bvToast.toast(
              errorMessage,
              {title: "Login Failed!", autoHideDelay: 1500, variant: "danger"}
          );
          that.input_submit_disabled = false;
      };
      
      that.input_submit_disabled = true;
      Requestor.postSync(path, data, s, f);
    },
    
    fetch_prereqs: function() {
      let path = "/api/user/get_tools_packet/";
      let data = {};
      let that = this;
      
      let s = function(response) {
        if (response.data.result) {
          let locations = (response.data.result.locations || []).map(function(d) {
            return {
              value: d.id, 
              text: d.name,
              short_name: d.short_name, 
              address: d.address, 
              email: d.email, 
              phone: d.phone, 
              allowed: d.allowed,
              sales_tax: parseFloat(d.sales_tax),
              is_main_warehouse: d.is_main_warehouse,
            }
          });
          locations.splice(0, 0, {value: null, text: "All", short: "None", address: null, email: null, phone: null, allowed: []});
          
          let locationsdict = locations.reduce((map, obj) => (map[obj.value] = obj.text, map), {});
          
          let sublocations = (response.data.result.sublocations || []).map(function(d){
            return {value: d.id, text: d.name, location_id: d.location_id}
          });
          let sublocationsdict = sublocations.reduce((map, obj) => (map[obj.value] = obj.text, map), {});
          
          let locsubs = (response.data.result.locsubs || []);
          let locsubsdict = locsubs.reduce((map, obj) => (map[obj.value] = obj.text, map), {});
          
          let vendors = (response.data.result.vendors || []);
          let vendorsdict = vendors.reduce((map, obj) => (map[obj.value] = obj.text, map), {});
          
          let categories = (response.data.result.categories || []);
          let categoriesdict = categories.reduce((map, obj) => (map[obj.value] = obj.text, map), {});
          
          let trucks = (response.data.result.trucks || []).map(function(d){
            return {value: d.value, text: d.text}
          });
          let trucksdict = trucks.reduce((map, obj) => (map[obj.value] = obj.text, map), {});
          
          let housetrucks = (response.data.result.housetrucks || []).map(function(d){
            return {value: d.value, text: d.text}
          });
          
          let deliverytrucks = (response.data.result.deliverytrucks || []).map(function(d){
            return {value: d.value, text: d.text}
          });
          
          let packet = {
            locations: locations,
            locationsdict: locationsdict,
            sublocations: sublocations,
            sublocationsdict: sublocationsdict,
            locsubs: locsubs,
            locsubsdict: locsubsdict,
            vendors: vendors,
            vendorsdict: vendorsdict,
            categories: categories,
            categoriesdict: categoriesdict,
            trucks: trucks,
            trucksdict: trucksdict,
            housetrucks: housetrucks,
            deliverytrucks: deliverytrucks
          }
          
          localStorage.setItem("packet", JSON.stringify(packet));
          that.$router.push({path: "/home/"});
        } else {
          that.$bvToast.toast(
            response.data.error || "Error loading data.", 
            {title: "Error", autoHideDelay: 1500, variant: "warning"}
          );
        }
      };
      
      let f = function(error) {
        console.log(error);
        that.$bvToast.toast(
          "Error loading data. Please try again.", 
          {title: "Error", autoHideDelay: 1500, variant: "warning"}
        );
      };
      
      Requestor.getSyncP(path, data, this, s, f);
    },
    
    sendPasswordResetRequest: function(e) {
      e.preventDefault();
      let that = this;
      let path = "/api/user/request-password-reset/";
      let data = { 
        username: this.resetEmail,
        frontend_url: window.location.origin
      };
      
      this.resetPasswordSending = true;
      
      let s = function(response) {
        that.resetPasswordSending = false;
        if (response.data.message) {
          that.showToast(response.data.message, 'success');
          setTimeout(() => {
            that.hideResetPasswordForm();
          }, 2000);
        }
      };
      
      let f = function(error) {
        that.resetPasswordSending = false;
        let errorMessage = "Failed to send reset link. Please try again.";
        if (error.response && error.response.data && error.response.data.error) {
          errorMessage = error.response.data.error;
        }
        that.$bvToast.toast(
          errorMessage,
          {title: "Error", autoHideDelay: 3000, variant: "danger"}
        );
      };
      
      Requestor.postSync(path, data, s, f);
    }
  },
  
  created: function() {
    const userToken = localStorage.getItem("userToken");
    if (userToken) {
      this.$router.push({path: "/home/"});
    }
  }
}
</script>

<style scoped>
.login-container {
  position: relative;
  min-height: 100vh;
  width: 100%;
  overflow-x: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Background Image */
.background-image {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image: url('https://m.media-amazon.com/images/I/81eSegKonhL._AC_SL1500_.jpg');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  filter: blur(4px);
  opacity: 0.8;
  z-index: -1;
  animation: backgroundZoom 20s ease-in-out infinite;
  transition: filter 0.3s ease;
}

.background-image.blur-active {
  filter: blur(8px);
}

@keyframes backgroundZoom {
  0%, 100% { transform: scale(1.05); }
  50% { transform: scale(1.1); }
}

/* Loading Overlay */
.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.85);
  backdrop-filter: blur(20px);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

.loading-card {
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.95), rgba(245, 245, 245, 0.95));
  border-radius: 24px;
  padding: 48px 56px;
  text-align: center;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5);
  animation: cardPop 0.5s cubic-bezier(0.68, -0.55, 0.265, 1.55);
  max-width: 400px;
  width: 90%;
}

@keyframes cardPop {
  from {
    opacity: 0;
    transform: scale(0.8);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

.loading-animation {
  margin-bottom: 32px;
}

.loading-ring {
  position: relative;
  width: 100px;
  height: 100px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
}

.ring {
  position: absolute;
  width: 100%;
  height: 100%;
  border: 4px solid transparent;
  border-radius: 50%;
  animation: ringRotate 1.5s linear infinite;
}

.ring:nth-child(1) {
  border-top-color: #4299e1;
  border-left-color: #4299e1;
  animation-duration: 1s;
}

.ring:nth-child(2) {
  border-right-color: #48bb78;
  border-bottom-color: #48bb78;
  width: 80%;
  height: 80%;
  top: 10%;
  left: 10%;
  animation-duration: 1.2s;
  animation-direction: reverse;
}

.ring:nth-child(3) {
  border-left-color: #ed8936;
  border-bottom-color: #ed8936;
  width: 60%;
  height: 60%;
  top: 20%;
  left: 20%;
  animation-duration: 0.8s;
}

@keyframes ringRotate {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.loading-logo {
  font-size: 32px;
  font-weight: bold;
  color: #2d3748;
  animation: logoPulse 1.5s ease-in-out infinite;
  z-index: 1;
}

@keyframes logoPulse {
  0%, 100% { transform: scale(1); opacity: 0.7; }
  50% { transform: scale(1.1); opacity: 1; }
}

.loading-text {
  margin-bottom: 24px;
}

.loading-text h3 {
  color: #2d3748;
  font-size: 1.3rem;
  margin-bottom: 8px;
  display: inline-block;
}

.loading-dots {
  display: inline-flex;
  margin-left: 8px;
}

.loading-dots .dot {
  width: 6px;
  height: 6px;
  background: #4299e1;
  border-radius: 50%;
  margin: 0 2px;
  animation: dotBounce 1.4s ease-in-out infinite;
}

.loading-dots .dot:nth-child(1) { animation-delay: 0s; }
.loading-dots .dot:nth-child(2) { animation-delay: 0.2s; }
.loading-dots .dot:nth-child(3) { animation-delay: 0.4s; }

@keyframes dotBounce {
  0%, 60%, 100% { transform: translateY(0); }
  30% { transform: translateY(-8px); }
}

.loading-progress {
  width: 100%;
  height: 4px;
  background: #e2e8f0;
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 20px;
}

.progress-bar {
  height: 100%;
  background: linear-gradient(90deg, #4299e1, #48bb78, #4299e1);
  background-size: 200% 100%;
  animation: progressShine 1.5s linear infinite;
  transition: width 0.3s ease;
  border-radius: 4px;
}

@keyframes progressShine {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

.loading-tips {
  margin-top: 20px;
}

.tip-text {
  color: #718096;
  font-size: 0.9rem;
  animation: tipFade 0.5s ease;
}

@keyframes tipFade {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.loading-overlay-leave-active {
  animation: fadeOut 0.3s ease;
}

@keyframes fadeOut {
  from { opacity: 1; }
  to { opacity: 0; }
}

/* Main Content */
.login-content {
  position: relative;
  display: grid;
  grid-template-columns: 1fr 1fr;
  width: 100%;
  max-width: 1200px;
  min-height: 80vh;
  height: auto;
  background: rgba(255, 255, 255, 0.52);
  border-radius: 20px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
  overflow: hidden;
  backdrop-filter: blur(10px);
  margin: 0 20px;
  opacity: 0;
  transform: translateY(20px);
  transition: all 0.6s cubic-bezier(0.68, -0.55, 0.265, 1.55);
}

.login-content.content-loaded {
  opacity: 1;
  transform: translateY(0);
}

.login-content.blur-content {
  filter: blur(5px);
  pointer-events: none;
}

/* LOGIN SECTION (LEFT CARD) - hides when reset shown */
.login-section {
  padding: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.55);
  transition: opacity 0.4s ease, visibility 0.4s ease;
  z-index: 2;
}

.login-section.hidden-card {
  opacity: 0;
  visibility: hidden;
  pointer-events: none;
}

/* BRAND SECTION (RIGHT CARD) - slides to LEFT when reset shown */
.brand-section {
  background: linear-gradient(135deg, #364050 50%, #ffffff 100%);
  color: white;
  padding: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  transition: transform 0.5s cubic-bezier(0.68, -0.55, 0.265, 1.55);
  transform: translateX(0);
  z-index: 2;
}

.brand-section.slide-to-left {
  transform: translateX(-100%);
}

.brand-section::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100" opacity="0.1"><text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" font-size="10">UT</text></svg>');
  background-size: 200px;
  opacity: 0.1;
  animation: rotatePattern 60s linear infinite;
}

@keyframes rotatePattern {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

/* RESET PASSWORD SECTION (appears from RIGHT) */
.reset-password-section {
  position: absolute;
  top: 0;
  right: 0;
  width: 50%;
  height: 100%;
  padding: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  transform: translateX(100%);
  transition: transform 0.5s cubic-bezier(0.68, -0.55, 0.265, 1.55);
  z-index: 3;
  border-radius: 0 20px 20px 0;
}

.reset-password-section.visible {
  transform: translateX(0);
}

.reset-password-container {
  width: 100%;
  max-width: 400px;
}

.back-to-login {
  background: none;
  border: none;
  color: #4299e1;
  cursor: pointer;
  font-size: 0.9rem;
  padding: 0 0 20px 0;
  display: flex;
  align-items: center;
  gap: 5px;
  transition: all 0.3s ease;
}

.back-to-login:hover {
  color: #3182ce;
  transform: translateX(-5px);
}

.reset-password-header {
  text-align: center;
  margin-bottom: 30px;
}

.reset-icon {
  font-size: 3rem;
  margin-bottom: 15px;
  animation: iconFloat 2s ease-in-out infinite;
}

@keyframes iconFloat {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-10px); }
}

.reset-password-header h2 {
  color: #2d3748;
  font-size: 1.8rem;
  margin-bottom: 10px;
}

.reset-password-header p {
  color: #718096;
  font-size: 0.9rem;
}

.reset-password-form {
  margin-bottom: 20px;
}

.reset-button {
  width: 100%;
  padding: 14px;
  font-size: 1rem;
  font-weight: 600;
  border-radius: 10px;
  background: linear-gradient(135deg, #4299e1, #3182ce);
  border: none;
  transition: all 0.3s ease;
}

.reset-button:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(66, 153, 225, 0.3);
}

.reset-button:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.reset-button.loading {
  background: linear-gradient(135deg, #3182ce, #2c5282);
}

.reset-info {
  text-align: center;
  padding: 20px;
  background: #f7fafc;
  border-radius: 10px;
  margin-top: 20px;
}

.reset-info p {
  color: #718096;
  font-size: 0.85rem;
  margin: 0;
}

.brand-content {
  text-align: center;
  z-index: 1;
  width: 100%;
}

.university-logo h2 {
  font-size: 1.8rem;
  font-weight: bold;
  margin-bottom: 8px;
  color: white;
}

.university-logo p {
  font-size: 1rem;
  opacity: 0.9;
  margin-bottom: 30px;
}

.divider {
  height: 2px;
  background: rgba(255, 255, 255, 0.3);
  margin: 30px 0;
  border-radius: 1px;
  transform: scaleX(0);
  animation: dividerExpand 0.8s ease-out 0.3s forwards;
}

@keyframes dividerExpand {
  to { transform: scaleX(1); }
}

.austin-section h3 {
  font-size: 1.5rem;
  font-weight: 300;
  letter-spacing: 3px;
  margin-bottom: 20px;
  color: white;
}

.venues {
  margin-bottom: 30px;
}

.venue-item {
  margin: 12px 0;
  font-size: 1rem;
  opacity: 0.9;
}

.venue-item strong {
  font-weight: 600;
}

.creek-section h3 {
  font-size: 1.3rem;
  font-weight: 300;
  letter-spacing: 3px;
  margin-bottom: 15px;
  color: white;
}

.login-form-container {
  width: 100%;
  max-width: 400px;
}

.login-header {
  text-align: center;
  margin-bottom: 40px;
}

.login-header h1 {
  font-size: 2.2rem;
  font-weight: 700;
  color: #2d3748;
  margin-bottom: 10px;
}

.login-header p {
  color: #718096;
  font-size: 1rem;
}

/* Password Input */
.password-input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.password-input-wrapper .form-input {
  flex: 1;
  padding-right: 45px;
}

.password-toggle-btn {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  cursor: pointer;
  font-size: 1.2rem;
  padding: 5px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  border-radius: 50%;
  width: 32px;
  height: 32px;
}

.password-toggle-btn:hover:not(:disabled) {
  background: rgba(0, 0, 0, 0.05);
  transform: translateY(-50%) scale(1.05);
}

.password-toggle-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.eye-icon {
  font-size: 1.1rem;
  line-height: 1;
}

.form-input {
  background: rgba(255, 255, 255, 0.9);
  border: 2px solid #e2e8f0;
  border-radius: 10px;
  font-size: 1rem;
  transition: all 0.3s ease;
}

.form-input:focus {
  background: white;
  border-color: #4299e1;
  box-shadow: 0 0 0 3px rgba(66, 153, 225, 0.1);
  transform: translateY(-2px);
}

.form-options {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 20px 0 30px;
}

.remember-checkbox {
  margin: 0;
}

.remember-checkbox >>> .custom-control-label {
  color: #4a5568;
  font-size: 0.9rem;
}

.forgot-password {
  color: #4299e1;
  text-decoration: none;
  font-size: 0.9rem;
  transition: all 0.3s ease;
  cursor: pointer;
  position: relative;
}

.forgot-password::after {
  content: '';
  position: absolute;
  width: 0;
  height: 2px;
  bottom: -2px;
  left: 0;
  background-color: #4299e1;
  transition: width 0.3s ease;
}

.forgot-password:hover::after {
  width: 100%;
}

.forgot-password:hover {
  color: #3182ce;
}

.login-button {
  width: 100%;
  padding: 14px;
  font-size: 1.1rem;
  font-weight: 600;
  border-radius: 10px;
  background: linear-gradient(135deg, #364050 60%, #cacaca 100%);
  border: none;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
}

.login-button::before {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  width: 0;
  height: 0;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.3);
  transform: translate(-50%, -50%);
  transition: width 0.6s, height 0.6s;
}

.login-button:hover::before {
  width: 300px;
  height: 300px;
}

.login-button:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 105, 139, 0.3);
}

.login-button:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.login-button.loading {
  background: linear-gradient(135deg, #2c3848 60%, #a0a0a0 100%);
}

.button-loader {
  display: inline-flex;
  gap: 4px;
  align-items: center;
}

.loader-dot {
  width: 8px;
  height: 8px;
  background: white;
  border-radius: 50%;
  animation: bounce 1.4s ease-in-out infinite;
}

.loader-dot:nth-child(1) { animation-delay: 0s; }
.loader-dot:nth-child(2) { animation-delay: 0.2s; }
.loader-dot:nth-child(3) { animation-delay: 0.4s; }

@keyframes bounce {
  0%, 60%, 100% { transform: translateY(0); }
  30% { transform: translateY(-6px); }
}

/* NEW: Failed Attempts Warning Styles */
.attempts-warning {
  margin-top: 20px;
  padding: 12px 16px;
  background: #fff3e0;
  border-left: 4px solid #ed8936;
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 0.85rem;
  color: #c05621;
  animation: warningPulse 0.5s ease;
}

.attempts-warning .warning-icon {
  font-size: 1.2rem;
}

@keyframes warningPulse {
  0% { opacity: 0; transform: translateY(-10px); }
  100% { opacity: 1; transform: translateY(0); }
}

/* Animations */
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.5s ease;
}
.fade-enter, .fade-leave-to {
  opacity: 0;
}

.fade-down-enter-active, .fade-down-leave-active {
  transition: all 0.6s ease;
}
.fade-down-enter, .fade-down-leave-to {
  opacity: 0;
  transform: translateY(-30px);
}

.fade-up-enter-active, .fade-up-leave-active {
  transition: all 0.6s ease 0.2s;
}
.fade-up-enter, .fade-up-leave-to {
  opacity: 0;
  transform: translateY(30px);
}

.slide-fade-enter-active, .slide-fade-leave-active {
  transition: all 0.8s cubic-bezier(0.68, -0.55, 0.265, 1.55);
}
.slide-fade-enter, .slide-fade-leave-to {
  opacity: 0;
  transform: translateX(-50px);
}

.form-field-enter-active, .form-field-leave-active {
  transition: all 0.4s ease;
}
.form-field-enter, .form-field-leave-to {
  opacity: 0;
  transform: translateX(-20px);
}

.location-selector-enter-active {
  animation: slideDown 0.5s ease forwards;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.button-pulse-enter-active {
  animation: buttonPulse 0.6s ease;
}

@keyframes buttonPulse {
  0% { transform: scale(0.95); opacity: 0; }
  50% { transform: scale(1.02); }
  100% { transform: scale(1); opacity: 1; }
}

.brand-slide-enter-active {
  transition: all 0.8s cubic-bezier(0.68, -0.55, 0.265, 1.55);
}
.brand-slide-enter, .brand-slide-leave-to {
  opacity: 0;
  transform: translateX(50px);
}

.logo-reveal-enter-active, .text-reveal-enter-active {
  transition: all 0.6s ease;
}
.logo-reveal-enter, .text-reveal-enter {
  opacity: 0;
  clip-path: inset(0 100% 0 0);
}
.logo-reveal-enter-to, .text-reveal-enter-to {
  opacity: 1;
  clip-path: inset(0 0 0 0);
}

.venue-item-enter-active {
  transition: all 0.5s ease 0.4s;
}
.venue-item-enter, .venue-item-leave-to {
  opacity: 0;
  transform: translateY(20px);
}

/* Toast */
.custom-toast {
  position: fixed;
  bottom: 30px;
  right: 30px;
  background: linear-gradient(135deg, #48bb78, #38a169);
  color: white;
  padding: 16px 24px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  gap: 12px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
  z-index: 2000;
  min-width: 280px;
}

.custom-toast.success {
  background: linear-gradient(135deg, #48bb78, #38a169);
}

.toast-icon {
  width: 24px;
  height: 24px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
}

.toast-content {
  flex: 1;
}

.toast-title {
  font-weight: bold;
  margin-bottom: 4px;
}

.toast-message {
  font-size: 0.9rem;
  opacity: 0.9;
}

.toast-slide-enter-active, .toast-slide-leave-active {
  transition: all 0.5s cubic-bezier(0.68, -0.55, 0.265, 1.55);
}
.toast-slide-enter, .toast-slide-leave-to {
  opacity: 0;
  transform: translateX(100px);
}

/* Responsive */
@media (max-width: 768px) {
  .login-content {
    grid-template-columns: 1fr;
    min-height: auto;
    margin: 10px;
  }
  
  .brand-section, .login-section {
    padding: 30px 20px;
  }
  
  .reset-password-section {
    width: 100%;
    border-radius: 20px;
  }
  
  .brand-section.slide-to-left {
    transform: translateX(-100%);
  }
  
  .university-logo h2 {
    font-size: 1.5rem;
  }
  
  .login-header h1 {
    font-size: 1.8rem;
  }
  
  .form-input {
    font-size: 16px;
  }

  .custom-toast {
    bottom: 20px;
    right: 20px;
    left: 20px;
    min-width: auto;
  }

  .loading-card {
    padding: 32px 24px;
  }

  .loading-ring {
    width: 80px;
    height: 80px;
  }

  .loading-logo {
    font-size: 24px;
  }
}

@media (max-width: 480px) {
  .login-content {
    margin: 10px;
  }
  
  .brand-section, .login-section {
    padding: 20px 15px;
  }
  
  .reset-password-section {
    padding: 20px 15px;
  }
  
  .form-options {
    flex-direction: column;
    gap: 15px;
    align-items: flex-start;
  }
  
  .university-logo h2 {
    font-size: 1.3rem;
  }
  
  .login-header h1 {
    font-size: 1.6rem;
  }
  
  .brand-content {
    padding: 0 10px;
  }
  
  .login-button {
    width: 100%;
  }
}
</style>


// WEBPACK FOOTER //
// src/components/importables/Login.vue